import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Space, Form, Select, Input, InputNumber, Button } from 'antd';
import range from 'lodash/range';

import { ApplicationUIContext } from '@yuhuinc/ui-library';

import { showFailure } from 'actions/notificationActions';

import { getTranslatedString } from 'helpers/i18n';

const { TextArea } = Input;

const StyledSpace = styled(Space)`
  width: 100%;

  > * {
    flex: 1;
  }

  .ant-input-number,
  .ant-input-number-group-wrapper {
    width: 100%;
  }
`;

const MIN_AMOUNT_PER_MONTH = 0;
const MAX_AMOUNT_PER_MONTH = 999999;
const MAX_PERCENTAGE = 100;
const MIN_DURATION_IN_MONTHS = 1;
const MAX_DURATION_IN_MONTHS = 99;

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const startingMonthOptions = range(1, 19).map((monthNumber) => ({
  value: monthNumber,
  label: getTranslatedString('landlordPortal.incentives.monthNOfLease', { n: monthNumber }),
}));

const CreateIncentive = ({
  initialValues = {},
  onSubmit = () => {},
  onCancel = () => {},
  onError = () => {},
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  function onFinish(incentive) {
    onSubmit({
      id: initialValues.id || `new-${Date.now()}`,
      name: getTranslatedString('landlordPortal.incentives.oneTimeIncentiveName'),
      calculation_type: incentive.calculation_type,
      description: incentive.description,
      starting_month: incentive.starting_month,
      duration_in_months: incentive.duration_in_months,
      amount_per_month: incentive.amount_per_month,
    });
  }

  function onFinishFailed({ errorFields }) {
    if (errorFields.length > 1) {
      return onError({
        description: getTranslatedString('errorsOnThisPage'),
      });
    }
  }

  return (
    <Form
      layout="vertical"
      size={isMobile && 'large'}
      form={form}
      name="create-incentive"
      scrollToFirstError
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
    >
      <StyledSpace direction="vertical" size="middle">
        <StyledSpace
          align="start"
          direction={isMobile ? 'vertical' : 'horizontal'}
          size={isMobile ? 16 : 10}
        >
          <Form.Item
            label={getTranslatedString('landlordPortal.incentives.calculationType')}
            name={'calculation_type'}
            rules={[requiredField]}
          >
            <Select
              options={[
                {
                  value: 'percentage',
                  label: getTranslatedString('landlordPortal.incentives.percentage'),
                },
                {
                  value: 'absolute',
                  label: getTranslatedString('landlordPortal.incentives.absolute'),
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={getTranslatedString('landlordPortal.incentives.amountPerMonth')}
            name={'amount_per_month'}
            rules={[requiredField]}
          >
            <InputNumber
              min={MIN_AMOUNT_PER_MONTH}
              max={
                values?.calculation_type === 'percentage' ? MAX_PERCENTAGE : MAX_AMOUNT_PER_MONTH
              }
              addonAfter={values?.calculation_type === 'percentage' ? '%' : null}
              addonBefore={values?.calculation_type === 'absolute' ? '$' : null}
            />
          </Form.Item>
        </StyledSpace>
        <StyledSpace
          align="start"
          direction={isMobile ? 'vertical' : 'horizontal'}
          size={isMobile ? 16 : 10}
        >
          <Form.Item
            label={getTranslatedString('landlordPortal.incentives.durationInMonths')}
            name={'duration_in_months'}
            rules={[requiredField]}
          >
            <InputNumber min={MIN_DURATION_IN_MONTHS} max={MAX_DURATION_IN_MONTHS} />
          </Form.Item>
          <Form.Item
            label={getTranslatedString('landlordPortal.incentives.starting')}
            name={'starting_month'}
            rules={[requiredField]}
          >
            <Select options={startingMonthOptions} />
          </Form.Item>
        </StyledSpace>
        <Form.Item
          label={getTranslatedString('landlordPortal.incentives.externalDescription')}
          name={'description'}
        >
          <TextArea rows={4} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            {initialValues.id ? getTranslatedString('save') : getTranslatedString('add')}
          </Button>
          <Button htmlType="button" onClick={onCancel}>
            {getTranslatedString('landlordPortal.incentives.cancel')}
          </Button>
        </Space>
      </StyledSpace>
    </Form>
  );
};

CreateIncentive.propTypes = {
  initialValues: PropTypes.any,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
};

export default connect(null, {
  showFailure,
})(CreateIncentive);
